import { useCallback } from 'react';
import { useTranslation as useTranslationBase } from 'react-i18next';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';
import { loadLanguage } from '@/config/i18n';
import type { SupportedLanguage } from '@/lib/languageUtils';

export const useLanguage = () => {
  const { i18n } = useTranslationBase();
  const { user } = useAuth();

  const changeLanguage = useCallback(
    async (lang: SupportedLanguage) => {
      try {
        // Defensive: a bare 'pt' arriving as a raw string maps to pt-PT (the
        // only Portuguese variant we ship post-retarget). Cast for the compare
        // since 'pt' is not a member of SupportedLanguage.
        const langToSave = (lang as string) === 'pt' ? 'pt-pt' : lang;

        // Load language resources if not already loaded
        if (langToSave !== 'en') {
          await loadLanguage(langToSave);
        }

        // Save to localStorage FIRST to ensure it's persisted
        try {
          localStorage.setItem('i18n_language', langToSave);
        } catch (e) {
          console.warn('Could not save language to localStorage:', e);
        }

        // Then change language in i18next
        await i18n.changeLanguage(langToSave);

        // Sync to database if user is logged in
        if (user && user.id) {
          try {
            // Update language preference in user_preferences table
            const { error } = await supabase
              .from('user_preferences')
              .upsert(
                {
                  user_id: user.id,
                  language: langToSave,
                  updated_at: new Date().toISOString(),
                },
                { onConflict: 'user_id' }
              );

            if (error) {
              console.warn('Could not sync language to database:', error);
              // Don't throw - we still want the language change to work locally
            }
          } catch (e) {
            // Table might not exist yet, silently continue
            console.warn('Language sync failed:', e);
          }
        }

        return true;
      } catch (error) {
        console.error('Error changing language:', error);
        return false;
      }
    },
    [i18n, user]
  );

  const currentLanguage = i18n.language as SupportedLanguage;

  const loadUserLanguage = useCallback(async () => {
    if (!user || !user.id) return;

    try {
      // Load language preference from database
      // maybeSingle (not single) — new users may not have a
      // user_preferences row yet, and .single() would noisily emit
      // PGRST116 in the console for a non-error path. maybeSingle returns
      // null without the warning.
      const { data, error } = await supabase
        .from('user_preferences')
        .select('language')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.warn('Could not load language preference:', error);
        return;
      }

      if (data && data.language) {
        // Load language resources if not already loaded
        if (data.language !== 'en') {
          await loadLanguage(data.language);
        }
        await i18n.changeLanguage(data.language);
        try {
          localStorage.setItem('i18n_language', data.language);
        } catch (e) {
          console.warn('Could not save language to localStorage:', e);
        }
      }
    } catch (e) {
      console.warn('Error loading user language preference:', e);
    }
  }, [user, i18n]);

  return {
    currentLanguage,
    changeLanguage,
    loadUserLanguage,
    i18n,
  };
};

// Re-export useTranslation for convenience
export const useTranslation = useTranslationBase;
